import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "assistant-conversation-panel-open"
const RESERVED_CLASS = "conversation-panel-reserved"
const HIGHLIGHT_CLASS = "conversation-panel-attention"
const HIGHLIGHT_DURATION = 900
const PANEL_SPACE = 320
const MIN_CONVERSATION_WIDTH = 688

export default class extends Controller {
  static targets = ["collapsed", "expanded", "block", "floating"]
  static values = { mode: { type: String, default: "floating" } }

  connect() {
    this._frame = this.element.closest("turbo-frame")
    this._composer = this._frame?.querySelector("#assistant-input-container")
    const stored = this._storedPreference()
    this._render(this._floating && (stored ?? this._fits()))
    this.layout()
    this._knownSourceUrls = this._sourceUrls()
    this._unseenSources = false
    this._sourceObserver = new MutationObserver(() => this._queueSourceFeedback())
    this._sourceObserver.observe(this.element, { childList: true, subtree: true })
    if (this._floating && this._composer) {
      this._layoutObserver = new ResizeObserver(() => this.layout())
      this._layoutObserver.observe(this._composer)
      this._layoutObserver.observe(this._frame)
    }
  }

  disconnect() {
    this._layoutObserver?.disconnect()
    this._sourceObserver?.disconnect()
    if (this._sourceFeedbackFrame != null) cancelAnimationFrame(this._sourceFeedbackFrame)
    this._sourceFeedbackFrame = null
    this._clearHighlight()
    this._frame?.classList.remove(RESERVED_CLASS)
  }

  open(event) {
    this._show(event.params?.key)
  }

  close() {
    if (!this._isOpen) return
    this._clearHighlight()
    this._render(false)
    this._remember(false)
    this.layout()
  }

  closeOnEscape(event) {
    if (event.defaultPrevented || this._anotherLayerOpen) return
    this.close()
  }

  layout() {
    if (!this._floating || !this._frame) return
    this._frame.classList.toggle(RESERVED_CLASS, this._isOpen && this._fits())
    if (!this.hasFloatingTarget || !this._composer) return

    const composerTop = this._composer.getBoundingClientRect().top
    const frameBottom = this._frame.getBoundingClientRect().bottom
    this.floatingTarget.style.bottom = `${Math.max(0, frameBottom - composerTop + 16)}px`
  }

  handleWindowClick(event) {
    const opener = event.target.closest("[data-conversation-panel-opener]")
    if (opener && this._sameFrame(opener)) {
      this._show(opener.dataset.conversationPanelOpener)
      return
    }
    if (this._overlaying && !this.element.contains(event.target)) this.close()
  }

  _sameFrame(element) {
    return element.closest("turbo-frame") === this._frame
  }

  _show(key) {
    this._render(true)
    this._remember(true)
    this.layout()
    this._focusBlock(key)
    if (key === "sources") {
      this._unseenSources = false
      this._clearNewSourcesIndicators()
    }
  }

  _focusBlock(key) {
    const block = this.blockTargets.find((target) => target.dataset.key === key)
    if (!block) return
    if (block.tagName === "DETAILS") block.open = true
    block.scrollIntoView({ block: "nearest", behavior: "smooth" })
    const replaying = this._highlightedBlock === block
    this._clearHighlight()
    if (replaying) block.getBoundingClientRect()
    block.classList.add(HIGHLIGHT_CLASS)
    this._highlightedBlock = block
    this._highlightTimeout = window.setTimeout(() => this._clearHighlight(), HIGHLIGHT_DURATION)
  }

  _clearHighlight() {
    window.clearTimeout(this._highlightTimeout)
    this._highlightedBlock?.classList.remove(HIGHLIGHT_CLASS)
    this._highlightedBlock = null
  }

  _render(open) {
    this.expandedTarget.hidden = !open
    if (this.hasCollapsedTarget) this.collapsedTarget.hidden = open
  }

  _sourceUrls() {
    return new Set(Array.from(this.element.querySelectorAll('[data-key="sources"] a[href]'), (link) => link.getAttribute("href")))
  }

  _queueSourceFeedback() {
    if (this._sourceFeedbackFrame != null) return

    this._sourceFeedbackFrame = requestAnimationFrame(() => {
      this._sourceFeedbackFrame = null
      this._showSourceFeedback()
    })
  }

  _showSourceFeedback() {
    const sourceUrls = this._sourceUrls()
    const addedUrls = new Set([...sourceUrls].filter((url) => !this._knownSourceUrls.has(url)))
    const firstSources = this._knownSourceUrls.size === 0
    this._knownSourceUrls = sourceUrls

    if (!addedUrls.size) {
      if (this._unseenSources) this._markNewSourcesIndicators(false)
      return
    }

    if (!this._isOpen) {
      this._unseenSources = true
      this._markNewSourcesIndicators()
      return
    }

    const block = this.element.querySelector('[data-key="sources"]')

    if (firstSources || !block.open) {
      block.open = true
      block.scrollIntoView({ block: "nearest", behavior: this._reducedMotion ? "auto" : "smooth" })
      if (!this._reducedMotion) block.animate?.([
        { opacity: 0, transform: "translateY(0.5rem)" },
        { opacity: 1, transform: "translateY(0)" }
      ], { duration: 250, easing: "ease-out" })
      return
    }

    if (this._reducedMotion) return
    for (const link of block.querySelectorAll("a[href]")) {
      if (addedUrls.has(link.getAttribute("href"))) link.closest("li")?.animate?.([
        { backgroundColor: "var(--color-blue-100)" },
        { backgroundColor: "transparent" }
      ], { duration: 900, easing: "ease-out" })
    }
  }

  _markNewSourcesIndicators(animate = true) {
    for (const indicator of this._frame?.querySelectorAll('[data-conversation-panel-key-param="sources"], [data-conversation-panel-opener="sources"]') || []) {
      if (indicator.querySelector("[data-new-sources-indicator]")) continue

      indicator.classList.add("relative")
      const dot = document.createElement("span")
      dot.dataset.newSourcesIndicator = ""
      dot.className = "absolute -top-0.5 -right-0.5 size-2 rounded-full bg-gobyellow ring-2 ring-white"
      dot.setAttribute("aria-hidden", "true")
      indicator.append(dot)
      if (animate && !this._reducedMotion) indicator.animate?.([
        { transform: "rotate(0deg)" },
        { transform: "rotate(-8deg)" },
        { transform: "rotate(6deg)" },
        { transform: "rotate(0deg)" }
      ], { duration: 900, easing: "ease-in-out" })
    }
  }

  _clearNewSourcesIndicators() {
    this._frame?.querySelectorAll("[data-new-sources-indicator]").forEach((dot) => dot.remove())
  }

  get _reducedMotion() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
  }

  _fits() {
    return !!this._frame && this._frame.clientWidth - PANEL_SPACE >= MIN_CONVERSATION_WIDTH
  }

  _remember(open) {
    if (!this._floating) return
    try {
      localStorage.setItem(STORAGE_KEY, open ? "1" : "0")
    } catch {}
  }

  _storedPreference() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored === null ? null : stored === "1"
    } catch {
      return null
    }
  }

  get _anotherLayerOpen() {
    return document.body.classList.contains("form-modal") || document.querySelector("dialog[open]") !== null
  }

  get _overlaying() {
    return this._floating && this._isOpen && !this._frame?.classList.contains(RESERVED_CLASS)
  }

  get _isOpen() {
    return !this.expandedTarget.hidden
  }

  get _floating() {
    return this.modeValue === "floating"
  }
}
